import { FieldDefinition } from './GenericEntityEditor';

// Product field definitions
export const productFieldDefinitions: FieldDefinition[] = [
  // Basic Information
  {
    key: 'name',
    label: 'Product Name',
    type: 'text',
    required: true,
    placeholder: 'Enter product name',
    description: 'The official name of the product'
  },
  {
    key: 'brand',
    label: 'Brand',
    type: 'text',
    placeholder: 'Enter brand name',
    description: 'The manufacturer or brand name'
  },
  {
    key: 'model',
    label: 'Model',
    type: 'text',
    placeholder: 'Enter model number/name',
    description: 'Specific model identifier'
  },
  {
    key: 'category',
    label: 'Category',
    type: 'select',
    required: true,
    options: [
      { value: 'electronics', label: 'Electronics' },
      { value: 'automotive', label: 'Automotive' },
      { value: 'apparel', label: 'Apparel' },
      { value: 'food', label: 'Food & Beverage' },
      { value: 'consumer_goods', label: 'Consumer Goods' },
      { value: 'industrial', label: 'Industrial' },
      { value: 'medical', label: 'Medical Devices' },
      { value: 'furniture', label: 'Furniture' },
      { value: 'toys', label: 'Toys & Games' },
      { value: 'sports', label: 'Sports & Recreation' }
    ],
    description: 'Primary product category'
  },
  {
    key: 'description',
    label: 'Description',
    type: 'textarea',
    placeholder: 'Enter product description',
    description: 'Detailed description of the product'
  },
  
  // Pricing Information
  {
    key: 'retail_price',
    label: 'Retail Price (USD)',
    type: 'number',
    min: 0,
    step: 0.01,
    placeholder: '0.00',
    description: 'Current retail price in USD'
  },
  {
    key: 'manufacturing_cost',
    label: 'Manufacturing Cost (USD)',
    type: 'number',
    min: 0,
    step: 0.01,
    placeholder: '0.00',
    description: 'Estimated manufacturing cost in USD'
  },
  
  // Environmental Impact
  {
    key: 'overall_environmental_impact',
    label: 'Environmental Impact',
    type: 'environmental',
    description: 'Overall environmental impact of the product'
  },
  
  // Labor Information
  {
    key: 'overall_labor_summary',
    label: 'Labor Summary',
    type: 'textarea',
    placeholder: 'Summary of labor conditions across supply chain',
    description: 'Overview of labor conditions and workforce'
  },
  {
    key: 'total_worker_count',
    label: 'Total Worker Count',
    type: 'number',
    min: 0,
    placeholder: '0',
    description: 'Total number of workers involved in production'
  },
  
  // Production Countries
  {
    key: 'primary_production_countries',
    label: 'Production Countries',
    type: 'json',
    placeholder: '[{"country": "China", "percentage": 65}]',
    description: 'List of primary production countries with percentages'
  },
  
  // Risk Assessment
  {
    key: 'risk_assessment',
    label: 'Risk Assessment',
    type: 'json',
    placeholder: '{"geopolitical_risk": "medium", "overall_risk_score": 55}',
    description: 'Comprehensive risk assessment data'
  },
  {
    key: 'overall_risk_score',
    label: 'Overall Risk Score',
    type: 'number',
    min: 0,
    max: 100,
    step: 0.1,
    placeholder: '0-100',
    description: 'Overall risk score from 0 (low risk) to 100 (high risk)'
  },
  
  // Supply Chain
  {
    key: 'supply_chain_concentration',
    label: 'Supply Chain Concentration',
    type: 'json',
    placeholder: '[{"country": "China", "percentage": 65, "value_usd": 290}]',
    description: 'Detailed supply chain concentration analysis by country'
  },
  
  // Analysis Status
  {
    key: 'analysis_status',
    label: 'Analysis Status',
    type: 'select',
    options: [
      { value: 'pending', label: 'Pending' },
      { value: 'analyzing', label: 'Analyzing' },
      { value: 'completed', label: 'Completed' },
      { value: 'failed', label: 'Failed' }
    ],
    description: 'Current analysis status'
  },
  {
    key: 'confidence_score',
    label: 'Confidence Score',
    type: 'number',
    min: 0,
    max: 100,
    step: 0.1,
    placeholder: '0-100',
    description: 'Confidence in the analysis results'
  }
];

// Component field definitions
export const componentFieldDefinitions: FieldDefinition[] = [
  // Basic Information
  {
    key: 'name',
    label: 'Component Name',
    type: 'text',
    required: true,
    placeholder: 'Enter component name',
    description: 'The official name of the component'
  },
  {
    key: 'category',
    label: 'Category',
    type: 'select',
    options: [
      { value: 'electronics', label: 'Electronics' },
      { value: 'mechanical', label: 'Mechanical' },
      { value: 'materials', label: 'Materials' },
      { value: 'software', label: 'Software' },
      { value: 'packaging', label: 'Packaging' },
      { value: 'fasteners', label: 'Fasteners' },
      { value: 'sensors', label: 'Sensors' },
      { value: 'displays', label: 'Displays' },
      { value: 'processors', label: 'Processors' },
      { value: 'memory', label: 'Memory' },
      { value: 'power', label: 'Power Systems' },
      { value: 'connectivity', label: 'Connectivity' }
    ],
    description: 'Component category'
  },
  {
    key: 'description',
    label: 'Description',
    type: 'textarea',
    placeholder: 'Enter component description',
    description: 'Detailed description of the component'
  },
  {
    key: 'function',
    label: 'Function',
    type: 'textarea',
    placeholder: 'What this component does',
    description: 'Primary function and purpose of the component'
  },
  
  // Cost Information
  {
    key: 'estimated_cost_min',
    label: 'Minimum Cost (USD)',
    type: 'number',
    min: 0,
    step: 0.01,
    placeholder: '0.00',
    description: 'Minimum estimated cost'
  },
  {
    key: 'estimated_cost_max',
    label: 'Maximum Cost (USD)',
    type: 'number',
    min: 0,
    step: 0.01,
    placeholder: '0.00',
    description: 'Maximum estimated cost'
  },
  
  // Physical Properties
  {
    key: 'weight',
    label: 'Weight (grams)',
    type: 'number',
    min: 0,
    step: 0.01,
    placeholder: '0.00',
    description: 'Component weight in grams'
  },
  {
    key: 'dimensions',
    label: 'Dimensions',
    type: 'json',
    placeholder: '{"length": 10, "width": 5, "height": 2, "unit": "mm"}',
    description: 'Physical dimensions of the component'
  },
  {
    key: 'materials',
    label: 'Materials',
    type: 'json',
    placeholder: '["Silicon", "Aluminum", "Plastic"]',
    description: 'List of materials used in the component'
  },
  
  // Geographic Information
  {
    key: 'country_of_origin',
    label: 'Country of Origin',
    type: 'text',
    placeholder: 'Primary production country',
    description: 'Primary country where this component is manufactured'
  },
  {
    key: 'production_locations',
    label: 'Production Locations',
    type: 'json',
    placeholder: '[{"country": "China", "percentage": 80, "major_companies": ["Foxconn"]}]',
    description: 'List of production locations with details'
  },
  
  // Environmental Impact
  {
    key: 'carbon_footprint_kg',
    label: 'Carbon Footprint (kg CO₂)',
    type: 'number',
    min: 0,
    step: 0.01,
    placeholder: '0.00',
    description: 'Carbon footprint in kg CO₂'
  },
  {
    key: 'energy_consumption_kwh',
    label: 'Energy Consumption (kWh)',
    type: 'number',
    min: 0,
    step: 0.01,
    placeholder: '0.00',
    description: 'Energy consumption in kWh'
  },
  {
    key: 'water_usage_liters',
    label: 'Water Usage (liters)',
    type: 'number',
    min: 0,
    step: 0.01,
    placeholder: '0.00',
    description: 'Water usage in liters'
  },
  {
    key: 'waste_generation_kg',
    label: 'Waste Generation (kg)',
    type: 'number',
    min: 0,
    step: 0.01,
    placeholder: '0.00',
    description: 'Waste generation in kg'
  },
  {
    key: 'recyclability_percentage',
    label: 'Recyclability (%)',
    type: 'number',
    min: 0,
    max: 100,
    step: 0.1,
    placeholder: '0-100',
    description: 'Recyclability percentage'
  },
  {
    key: 'renewable_energy_percentage',
    label: 'Renewable Energy (%)',
    type: 'number',
    min: 0,
    max: 100,
    step: 0.1,
    placeholder: '0-100',
    description: 'Renewable energy used in production'
  },
  
  // Labor Requirements
  {
    key: 'worker_count',
    label: 'Worker Count',
    type: 'number',
    min: 0,
    placeholder: '0',
    description: 'Number of workers required for production'
  },
  {
    key: 'skill_level',
    label: 'Skill Level',
    type: 'select',
    options: [
      { value: 'low', label: 'Low' },
      { value: 'medium', label: 'Medium' },
      { value: 'high', label: 'High' },
      { value: 'expert', label: 'Expert' }
    ],
    description: 'Required skill level for production'
  },
  {
    key: 'average_wage_usd_hour',
    label: 'Average Wage (USD/hour)',
    type: 'number',
    min: 0,
    step: 0.01,
    placeholder: '0.00',
    description: 'Average wage in USD per hour'
  },
  {
    key: 'working_hours_per_week',
    label: 'Working Hours/Week',
    type: 'number',
    min: 0,
    max: 168,
    step: 0.1,
    placeholder: '40',
    description: 'Working hours per week'
  },
  {
    key: 'working_conditions',
    label: 'Working Conditions',
    type: 'select',
    options: [
      { value: 'poor', label: 'Poor' },
      { value: 'fair', label: 'Fair' },
      { value: 'good', label: 'Good' },
      { value: 'excellent', label: 'Excellent' }
    ],
    description: 'Working conditions rating'
  },
  {
    key: 'safety_rating',
    label: 'Safety Rating',
    type: 'select',
    options: [
      { value: 'poor', label: 'Poor' },
      { value: 'fair', label: 'Fair' },
      { value: 'good', label: 'Good' },
      { value: 'excellent', label: 'Excellent' }
    ],
    description: 'Safety rating for production'
  },
  {
    key: 'automation_level',
    label: 'Automation Level',
    type: 'select',
    options: [
      { value: 'manual', label: 'Manual' },
      { value: 'semi-automated', label: 'Semi-Automated' },
      { value: 'automated', label: 'Automated' }
    ],
    description: 'Level of automation in production'
  },
  
  // Design and Manufacturing
  {
    key: 'design_rationale',
    label: 'Design Rationale',
    type: 'textarea',
    placeholder: 'Why this component was chosen, trade-offs made',
    description: 'Explanation of design decisions and trade-offs'
  },
  {
    key: 'complexity_level',
    label: 'Complexity Level',
    type: 'select',
    options: [
      { value: 'low', label: 'Low' },
      { value: 'medium', label: 'Medium' },
      { value: 'high', label: 'High' },
      { value: 'very_high', label: 'Very High' }
    ],
    description: 'Manufacturing complexity level'
  },
  {
    key: 'manufacturing_time_days',
    label: 'Manufacturing Time (days)',
    type: 'number',
    min: 0,
    step: 0.1,
    placeholder: '0',
    description: 'Time required for manufacturing in days'
  },
  
  // Analysis Status
  {
    key: 'analysis_status',
    label: 'Analysis Status',
    type: 'select',
    options: [
      { value: 'pending', label: 'Pending' },
      { value: 'analyzing', label: 'Analyzing' },
      { value: 'completed', label: 'Completed' },
      { value: 'failed', label: 'Failed' }
    ],
    description: 'Current analysis status'
  }
];

// Manufacturing Process field definitions
export const processFieldDefinitions: FieldDefinition[] = [
  // Basic Information
  {
    key: 'name',
    label: 'Process Name',
    type: 'text',
    required: true,
    placeholder: 'Enter process name',
    description: 'The name of the manufacturing process'
  },
  {
    key: 'category',
    label: 'Category',
    type: 'select',
    options: [
      { value: 'fabrication', label: 'Fabrication' },
      { value: 'assembly', label: 'Assembly' },
      { value: 'testing', label: 'Testing' },
      { value: 'packaging', label: 'Packaging' },
      { value: 'machining', label: 'Machining' },
      { value: 'molding', label: 'Molding' },
      { value: 'coating', label: 'Coating' },
      { value: 'welding', label: 'Welding' },
      { value: 'electronics', label: 'Electronics' },
      { value: 'quality_control', label: 'Quality Control' }
    ],
    description: 'Process category'
  },
  {
    key: 'description',
    label: 'Description',
    type: 'textarea',
    placeholder: 'Enter process description',
    description: 'Detailed description of the process'
  },
  {
    key: 'overview',
    label: 'Overview',
    type: 'textarea',
    placeholder: 'Brief overview of what this process does',
    description: 'High-level overview of the process'
  },
  
  // Process Details
  {
    key: 'complexity_level',
    label: 'Complexity Level',
    type: 'select',
    options: [
      { value: 'low', label: 'Low' },
      { value: 'medium', label: 'Medium' },
      { value: 'high', label: 'High' },
      { value: 'very_high', label: 'Very High' }
    ],
    description: 'Process complexity level'
  },
  {
    key: 'automation_level',
    label: 'Automation Level',
    type: 'select',
    options: [
      { value: 'manual', label: 'Manual' },
      { value: 'semi-automated', label: 'Semi-Automated' },
      { value: 'automated', label: 'Automated' },
      { value: 'fully_automated', label: 'Fully Automated' }
    ],
    description: 'Level of automation'
  },
  {
    key: 'precision_required',
    label: 'Precision Required',
    type: 'select',
    options: [
      { value: 'low', label: 'Low' },
      { value: 'medium', label: 'Medium' },
      { value: 'high', label: 'High' },
      { value: 'ultra_high', label: 'Ultra High' }
    ],
    description: 'Precision requirements'
  },
  
  // Timing and Cost
  {
    key: 'typical_duration_hours',
    label: 'Typical Duration (hours)',
    type: 'number',
    min: 0,
    step: 0.1,
    placeholder: '0.0',
    description: 'Typical process duration in hours'
  },
  {
    key: 'setup_time_hours',
    label: 'Setup Time (hours)',
    type: 'number',
    min: 0,
    step: 0.1,
    placeholder: '0.0',
    description: 'Setup time required in hours'
  },
  {
    key: 'cost_per_unit',
    label: 'Cost per Unit (USD)',
    type: 'number',
    min: 0,
    step: 0.01,
    placeholder: '0.00',
    description: 'Cost per unit processed'
  },
  
  // Equipment and Facilities
  {
    key: 'equipment_required',
    label: 'Equipment Required',
    type: 'json',
    placeholder: '[{"name": "CNC Machine", "type": "machining", "cost": 50000}]',
    description: 'List of required equipment'
  },
  {
    key: 'equipment_cost',
    label: 'Equipment Cost (USD)',
    type: 'number',
    min: 0,
    step: 0.01,
    placeholder: '0.00',
    description: 'Total equipment cost'
  },
  
  // Labor Requirements
  {
    key: 'workers_required',
    label: 'Workers Required',
    type: 'number',
    min: 0,
    placeholder: '0',
    description: 'Number of workers required'
  },
  {
    key: 'skill_level_required',
    label: 'Skill Level Required',
    type: 'select',
    options: [
      { value: 'low', label: 'Low' },
      { value: 'medium', label: 'Medium' },
      { value: 'high', label: 'High' },
      { value: 'expert', label: 'Expert' }
    ],
    description: 'Required skill level'
  },
  {
    key: 'training_time_hours',
    label: 'Training Time (hours)',
    type: 'number',
    min: 0,
    step: 0.1,
    placeholder: '0.0',
    description: 'Training time required in hours'
  },
  
  // Environmental Impact
  {
    key: 'energy_consumption_kwh',
    label: 'Energy Consumption (kWh)',
    type: 'number',
    min: 0,
    step: 0.01,
    placeholder: '0.00',
    description: 'Energy consumption per unit'
  },
  {
    key: 'water_usage_liters',
    label: 'Water Usage (liters)',
    type: 'number',
    min: 0,
    step: 0.01,
    placeholder: '0.00',
    description: 'Water usage per unit'
  },
  {
    key: 'waste_generated_kg',
    label: 'Waste Generated (kg)',
    type: 'number',
    min: 0,
    step: 0.01,
    placeholder: '0.00',
    description: 'Waste generated per unit'
  },
  {
    key: 'carbon_footprint_kg',
    label: 'Carbon Footprint (kg CO₂)',
    type: 'number',
    min: 0,
    step: 0.01,
    placeholder: '0.00',
    description: 'Carbon footprint per unit'
  },
  
  // Quality and Safety
  {
    key: 'hazard_level',
    label: 'Hazard Level',
    type: 'select',
    options: [
      { value: 'low', label: 'Low' },
      { value: 'medium', label: 'Medium' },
      { value: 'high', label: 'High' },
      { value: 'extreme', label: 'Extreme' }
    ],
    description: 'Safety hazard level'
  },
  {
    key: 'defect_rate_percentage',
    label: 'Defect Rate (%)',
    type: 'number',
    min: 0,
    max: 100,
    step: 0.01,
    placeholder: '0.00',
    description: 'Typical defect rate percentage'
  },
  {
    key: 'yield_percentage',
    label: 'Yield (%)',
    type: 'number',
    min: 0,
    max: 100,
    step: 0.01,
    placeholder: '100.00',
    description: 'Process yield percentage'
  }
];

// Validation functions
export const validationFunctions = {
  positiveNumber: (value: number) => {
    if (value < 0) return 'Value must be positive';
    return null;
  },
  
  percentage: (value: number) => {
    if (value < 0 || value > 100) return 'Value must be between 0 and 100';
    return null;
  },
  
  validJSON: (value: string) => {
    try {
      JSON.parse(value);
      return null;
    } catch {
      return 'Must be valid JSON';
    }
  },
  
  nonEmptyString: (value: string) => {
    if (!value || value.trim().length === 0) return 'This field is required';
    return null;
  }
};

// Add validation to field definitions
productFieldDefinitions.forEach(field => {
  if (field.type === 'number' && field.min === 0) {
    field.validation = validationFunctions.positiveNumber;
  }
  if (field.key.includes('percentage') || field.max === 100) {
    field.validation = validationFunctions.percentage;
  }
  if (field.type === 'json') {
    field.validation = validationFunctions.validJSON;
  }
});

componentFieldDefinitions.forEach(field => {
  if (field.type === 'number' && field.min === 0) {
    field.validation = validationFunctions.positiveNumber;
  }
  if (field.key.includes('percentage') || field.max === 100) {
    field.validation = validationFunctions.percentage;
  }
  if (field.type === 'json') {
    field.validation = validationFunctions.validJSON;
  }
});

processFieldDefinitions.forEach(field => {
  if (field.type === 'number' && field.min === 0) {
    field.validation = validationFunctions.positiveNumber;
  }
  if (field.key.includes('percentage') || field.max === 100) {
    field.validation = validationFunctions.percentage;
  }
  if (field.type === 'json') {
    field.validation = validationFunctions.validJSON;
  }
}); 